import { Component, OnInit, OnDestroy, ViewEncapsulation, signal } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { Observable, forkJoin, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { ClientIntegrationService } from '../../../services/client-integration.service';
import { ToastService } from '../../../services/toast.service';
import { ClientInformation } from '../../../models/client-information';
import { Integration } from '../../../models/integration';

import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';

const _flatten = filter => {
  const filters = filter.filters;
  if (filters) {
    return filters.reduce((acc, curr) => acc.concat(curr.filters ? _flatten(curr) : [curr]), []);
  }
  return [];
};

@Component({
  selector: 'app-undelete',
  templateUrl: './undelete.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./undelete.component.scss'],
  providers: [ClientIntegrationService],
  standalone: false,
})
export class UndeleteComponent implements OnInit, OnDestroy {

  public gridLoading = signal<boolean>(true);
  integrations: Integration[];
  selectedIntegration: Integration;
  integrationGridSettings = signal<GridSettings>(null);

  componentDestroyed$ = new Subject<boolean>();

  showActiveClients = true;
  hasNoIntegrations = false;
  showGoToRevisionHistory = false;

  activeClients: ClientInformation[];
  inactiveClients: ClientInformation[];
  selectedClient: ClientInformation;
  clientSelected = false;
  clientsFiltered: Observable<ClientInformation[]>;
  inactiveClientsFiltered: Observable<ClientInformation[]>;
  clientFormControl = new UntypedFormControl();
  inactiveClientFormControl = new UntypedFormControl();

  hasIntegrations = false;

  pageLoading = true;

  options = {
    autoClose: true,
    keepAfterRouteChange: true,
  };

  constructor(
    protected clientService: ClientIntegrationService,
    protected toastService: ToastService,
    private route: ActivatedRoute,
    private location: Location,
    private router: Router,
  ) {
    this.integrations = [];
    this.activeClients = [];
    this.inactiveClients = [];
    // this.clients = [];
  }

  ngOnInit(): void {
    this.integrationGridSettings.set(this.setupGrid());
    this.getAllClients();
  }

  getAllClients(): void {
    forkJoin({
      data1: this.clientService.getAllClients(),
      data2: this.clientService.getAllDeletedClients(),
    })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: ({ data1, data2 }) => {
          this.activeClients = data1;
          this.inactiveClients = data2;
          this.clientsFiltered = this.clientFormControl.valueChanges.pipe(
            startWith(''),
            map(value => typeof value === 'string' ? value : value.name),
            map(name => name ? this.filterClients(name) : this.activeClients.slice()),
          );
          this.inactiveClientsFiltered = this.inactiveClientFormControl.valueChanges.pipe(
            startWith(''),
            map(value => typeof value === 'string' ? value : value.name),
            map(name => name ? this.filterInactiveClients(name) : this.inactiveClients.slice()),
          );
          this.pageLoading = false;
        }, error: (_error) => {
          this.pageLoading = false;
        },
      });
  }

  clearBasics(): void {
    this.selectedIntegration = null;
    this.hasIntegrations = false;
    this.showGoToRevisionHistory = false;
    this.selectedClient = null;
    this.clientSelected = false;
    this.integrations = [];
    this.clientFormControl.setValue('');
    this.inactiveClientFormControl.setValue('');
    this.hasNoIntegrations = false;
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  onToggleActiveClientChange(value: any): void {
    if (value === 'true') {
      this.showActiveClients = true;
    } else {
      this.showActiveClients = false;
    }
    this.clearBasics();
  }

  editClient(): void {
    this.clearBasics();
  }

  selectClient(c: ClientInformation): void {
    this.pageLoading = true;
    this.selectedClient = c;
    this.clientSelected = true;
    this.getIntegrations();
  }

  displayFnClient(x: ClientInformation): string {
    return x && x.clientDisplayName ? x.clientDisplayName : '';
  }

  displayFnInactiveClient(x: ClientInformation): string {
    return x && x.clientDisplayName ? x.clientDisplayName : '';
  }

  private filterClients(name: string): ClientInformation[] {
    const filterValue = name.toLowerCase();
    return this.activeClients.filter(option => option.clientDisplayName.toLowerCase().includes(filterValue));
  }

  private filterInactiveClients(name: string): ClientInformation[] {
    const filterValue = name.toLowerCase();
    return this.inactiveClients.filter(option => option.clientDisplayName.toLowerCase().includes(filterValue));
  }

  selectionChangeHandler(event: Integration[]): void {
    if (event.length == 0) {
      this.selectedIntegration = null;
      return;
    }
    this.selectedIntegration = event[0];
  }

  clickUndeleteButton(): void {
    if (!this.selectedIntegration) {
      this.toastService.toastCreate('Integration not selected', 'Warning', this.options);
    }
    else if (!this.showActiveClients) {
      this.clientService.undeleteClient(this.selectedClient).subscribe({
        next: (_value) => {
          this.toastService.toastCreate('Client Undeleted.', 'Success', this.options);
          this.undeleteIntegration();
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to undelete client.', 'Warning', this.options);
        },
      });
    }
    else {
      if (this.selectedIntegration.deleted) {
        this.undeleteIntegration();
      }
      else {
        this.toastService.toastCreate('Integration already active.', 'Warning', this.options);
      }
    }
  }

  undeleteIntegration(): void {
    this.clientService.undeleteIntegration(this.selectedIntegration).subscribe({
      next: (_value) => {
        this.toastService.toastCreate('Integration Undeleted.', 'Success', this.options);
        this.router.navigate(['editConfig', this.selectedClient.clientDisplayName, this.selectedIntegration.name]);
      },
      error: (_error) => {
        this.toastService.toastCreate('Failed to undelete Integration.', 'Warning', this.options);
      },
    });
  }

  clickUndeleteClientButton(): void {
    this.clientService.undeleteClient(this.selectedClient).subscribe({
      next: (_value) => {
        this.toastService.toastCreate('Client Undeleted.', 'Success', this.options);
        this.router.navigate(['/add/integrationById', this.selectedClient.clientInformationId]);
      },
      error: (_error) => {
        this.toastService.toastCreate('Failed to undelete Client.', 'Warning', this.options);
      },
    });
  }

  clickAddIntegration(): void {
    this.router.navigate(['/add/integrationById', this.selectedClient.clientInformationId]);
  }

  clickGoToClient(): void {
    this.router.navigate(['/editConfig', this.selectedClient.clientDisplayName]);
  }

  getIntegrations(): void {
    if (this.selectedClient == null) {
      return;
    }

    this.clientService.getUniqueIntegrationsByClientId(this.selectedClient.clientInformationId)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (d) => {
            if (d.length > 0) {
              const aIntegrations = d.filter(i => i.deleted == false);
              if (aIntegrations.length > 0) {
                const aN = aIntegrations.map(i => i.name);
                const inactive = d.filter(i => aN.indexOf(i.name) == -1);
                if (inactive.length > 0) {
                  this.integrations = inactive;
                  this.hasIntegrations = true;
                  this.showGoToRevisionHistory = false;
                  
                  this.integrationGridSettings.set(this.integrationGridSettings().setData(this.integrations));
                }
                else {
                  this.showGoToRevisionHistory = true;
                  this.hasNoIntegrations = true;
                }
              }
              else {
                this.integrations = d;
                this.hasIntegrations = true;

                this.integrationGridSettings.set(this.integrationGridSettings().setData(this.integrations));
              }
            }
            else {
              this.hasNoIntegrations = true;
            }
          }, error: (_error) => {
            this.toastService.toastCreate('Failed to loan Integrations for client.', 'Warning', this.options);
          }, complete: () => {
            this.integrationGridSettings().loading.set(false);
            this.pageLoading = false;
          },
        });
  }

  setupGrid(): GridSettings {
    return new GridSettings({
      loading: this.gridLoading,
      selection: {
        enabled: true,
        mode: 'single',
        headerText: null,
        selectionType: 'button',
        showSelectAll: false,
        buttonClass: null,
        iconClass: null,
        text: 'Select',
        preselectedItems: null,
        allowRowSelect: true,
      },
      dataKey: 'integrationId',
      data: this.integrations,
      columns: [
        new GridColumnData({ field: 'integrationId', header: 'Integration Id'}),
        new GridColumnData({ field: 'name', header: 'Integration Name'}),
        new GridColumnData({ filterable: false, field: 'updatedDate', header: 'Updated Date', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
        new GridColumnData({ field: 'dataSourceTypeId', header: 'Data Source Type'}),
        new GridColumnData({ field: 'serverId', header: 'Server'}),
        new GridColumnData({ field: 'locked', header: 'Locked'}),
        new GridColumnData({ field: 'deleted', header: 'Deleted'}),
      ],
      sortMode: 'single',
      filter: {
        enabled: true,
        display: 'menu',
        mode: 'row',
      },
    });
  }

}
